// ── AllRatesPage ──
// The index rates (SOFR, Prime, BSBY…) in dbo.Rates that interest statements,
// the prepopulate procedure and scheduled payments read.
//
// How a rate is used depends on the TERM's Rate Reset:
//   Monthly (default) — the LAST rate dated in a month applies to the whole of
//                       the next month. Earlier rates in that month are ignored.
//   Daily             — each rate applies from its own date until the next one.
// Rates are stored as decimals (0.0725 = 7.25%); this page shows percent.
import { useState, useEffect } from "react";
import { B } from "../../../shared/theme";
import { fmtDate } from "../../../shared/format";
import { Btn, Card, FInput, FSelect, Icon, STh, SectionHeader, Td, TdNum, Th } from "../../../shared/ui";
import { useApp } from "../../../shared/appContext";

export const AllRatesPage = () => {
  const { api, role, setModal, setConfirm, delRec } = useApp();

  const [rows,        setRows]        = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [sortKey,     setSortKey]     = useState("date");
  const [sortDir,     setSortDir]     = useState("desc");
  const [search,      setSearch]      = useState("");
  const [filterIndex, setFilterIndex] = useState("");
  const [filterYear,  setFilterYear]  = useState("");

  const load = async () => {
    setLoading(true);
    try { setRows(await api.get("getRates")); } catch (e) { setRows([]); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = key => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  };

  const indexNames = [...new Set(rows.map(r => r.index_name).filter(Boolean))].sort();
  const years      = [...new Set(rows.map(r => String(r.date || "").slice(0, 4)).filter(Boolean))].sort().reverse();

  const displayed = rows
    .filter(r => {
      const q = search.toLowerCase();
      return (!q || (r.index_name || "").toLowerCase().includes(q)
                 || (r.source || "").toLowerCase().includes(q)
                 || String(r.date || "").includes(q))
          && (!filterIndex || r.index_name === filterIndex)
          && (!filterYear  || String(r.date || "").startsWith(filterYear));
    })
    .sort((a, b) => {
      const av = a[sortKey] ?? "", bv = b[sortKey] ?? "";
      if (typeof av === "number" && typeof bv === "number") return sortDir === "asc" ? av - bv : bv - av;
      return sortDir === "asc" ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });

  // A month with more than one rate for an index: under Monthly reset only the
  // LAST one is used, so flag the earlier ones rather than let them look live.
  const lastInMonth = {};
  rows.forEach(r => {
    const k = (r.index_name || "") + "|" + String(r.date || "").slice(0, 7);
    if (!lastInMonth[k] || String(r.date) > String(lastInMonth[k])) lastInMonth[k] = r.date;
  });
  const supersededMonthly = r => {
    const k = (r.index_name || "") + "|" + String(r.date || "").slice(0, 7);
    return String(r.date) !== String(lastInMonth[k]);
  };

  return <>
    <SectionHeader title="Index Rates" count={displayed.length} />

    <Card style={{ marginBottom:16, padding:16 }}>
      <div style={{ display:"flex", gap:12, alignItems:"center", flexWrap:"wrap" }}>
        <div style={{ width:240 }}><FInput placeholder="Search..." value={search} onChange={setSearch} /></div>
        <div style={{ width:160 }}>
          <FSelect value={filterIndex} onChange={setFilterIndex}
            options={[{ value:"", label:"All Indexes" }, ...indexNames.map(i => ({ value:i, label:i }))]} />
        </div>
        <div style={{ width:120 }}>
          <FSelect value={filterYear} onChange={setFilterYear}
            options={[{ value:"", label:"All Years" }, ...years.map(y => ({ value:y, label:y }))]} />
        </div>
        <div style={{ flex:1 }} />
        {role.canEdit && <Btn variant="primary" onClick={() => setModal({ type:"rate-add", onDone:load })}>+ Add Rate</Btn>}
      </div>
      <div style={{ marginTop:10, fontSize:12, color:"var(--color-text-secondary)", lineHeight:1.5 }}>
        <b>Monthly</b> reset terms use the <b>last</b> rate dated in a month for all of the following month.{" "}
        <b>Daily</b> reset terms use each rate from its own date until the next one. Rows marked{" "}
        <span style={{ fontStyle:"italic" }}>not used by Monthly</span> have a later rate in the same month.
      </div>
    </Card>

    <Card style={{ overflowX:"auto" }}>
      {loading
        ? <div style={{ padding:32, textAlign:"center", color:"var(--color-text-secondary)" }}>Loading...</div>
        : <table style={{ width:"100%", borderCollapse:"collapse", whiteSpace:"nowrap" }}>
            <thead><tr>
              <STh label="Date"   sortKey="date"       currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
              <STh label="Index"  sortKey="index_name" currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
              <STh label="Rate"   sortKey="rate"       currentKey={sortKey} currentDir={sortDir} onSort={toggle} numeric />
              <STh label="Source" sortKey="source"     currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
              <Th></Th>
              <Th></Th>
            </tr></thead>
            <tbody>
              {displayed.map(r => (
                <tr key={r.id} style={{ cursor:"pointer" }}
                  onClick={() => setModal({ type:"rate-view", rec:r, onDone:load })}
                  onMouseEnter={e => e.currentTarget.style.background = "var(--color-background-secondary)"}
                  onMouseLeave={e => e.currentTarget.style.background = ""}>
                  <Td><span style={{ fontFamily:"var(--font-mono)", fontSize:12 }}>{fmtDate(r.date)}</span></Td>
                  <Td>
                    <span style={{ background:B.cyanLight, color:B.cyanDark, padding:"2px 8px", borderRadius:4, fontSize:12, fontWeight:600 }}>
                      {r.index_name || "—"}
                    </span>
                  </Td>
                  <TdNum style={{ fontWeight:500 }}>{r.rate != null ? (Number(r.rate) * 100).toFixed(4) + "%" : "—"}</TdNum>
                  <Td style={{ color:"var(--color-text-secondary)", fontSize:12 }}>{r.source || "—"}</Td>
                  <Td style={{ fontSize:11, fontStyle:"italic", color:"var(--color-text-secondary)" }}>
                    {supersededMonthly(r) ? "not used by Monthly" : ""}
                  </Td>
                  <Td>
                    <div style={{ display:"flex", gap:6, justifyContent:"flex-end" }} onClick={e => e.stopPropagation()}>
                      <Btn onClick={() => setModal({ type:"rate-view", rec:r, onDone:load })}><Icon type="view" /></Btn>
                      {role.canEdit && <Btn onClick={() => setModal({ type:"rate-edit", rec:r, onDone:load })}><Icon type="edit" /></Btn>}
                      {role.canDelete && <Btn variant="danger" onClick={() => setConfirm({
                        msg:`Delete the ${r.index_name || ""} rate dated ${fmtDate(r.date)}? Statements generated after this will no longer see it.`,
                        onConfirm: async () => { await delRec("Rates", r.id, "Deleted rate", load); setConfirm(null); },
                      })}><Icon type="delete" /></Btn>}
                    </div>
                  </Td>
                </tr>
              ))}
              {!displayed.length && (
                <tr><td colSpan={6} style={{ textAlign:"center", padding:32, color:"var(--color-text-secondary)", fontSize:13 }}>
                  No rates found.
                </td></tr>
              )}
            </tbody>
          </table>}
    </Card>
  </>;
};
